import { AlertTriangle, Search, Wand2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { capacityStatusOf } from '@obraplan/core/domain/planning/capacity-calculator';
import type {
  BoardActivity,
  BoardDay,
  BoardRow,
  BoardService,
} from '@obraplan/core/domain/planning/weekly-board';
import { Hours, QUARTERS_PER_HOUR } from '@obraplan/core/domain/shared/hours';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { searchKey } from '@/lib/search-key';

/**
 * Adicionar alocação — recurso, atividade e **as horas de cada dia**.
 *
 * A atividade é escolhida **pela árvore** — torre, pavimento, disciplina —, que é como
 * quem planeja pensa a obra; a busca por texto existe para quem já sabe o nome. Atividade
 * concluída ou cancelada não aparece na lista (RN-084): esconder é melhor do que deixar
 * escolher e recusar depois.
 *
 * "Preencher a jornada" é atalho, não automação: preenche os campos e deixa quem planeja
 * revisar antes de enviar (RN-089).
 */

export interface AllocationDay {
  readonly date: string;
  readonly quarterHours: number;
  /**
   * Quanto se espera produzir neste dia, na unidade da frente.
   *
   * Ausente quando a frente não é medida, ou quando quem planeja não quis prometer
   * número nenhum — meta em branco é um estado legítimo, e não zero.
   */
  readonly quantity?: number;
}

export interface AllocationSubmit {
  readonly resourceId: string;
  readonly activityId: string;
  /**
   * O serviço escolhido — o que a obra chama de atividade (ADR-135).
   *
   * Opcional no tipo porque dois lugares enviam esta mesma forma: o painel, que exige o
   * serviço antes de habilitar o botão, e a grade, que digita horas numa linha que já
   * existe — e pode ser uma linha antiga, de antes do cadastro de serviços.
   */
  readonly serviceId?: string;
  readonly days: readonly AllocationDay[];
  readonly acknowledgeRestriction?: boolean;
}

export interface AllocationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: readonly BoardRow[];
  days: readonly BoardDay[];
  activities: readonly BoardActivity[];
  /** O catálogo que a obra alcança — a lista de onde o serviço é escolhido (RN-230). */
  services: readonly BoardService[];
  /** Recurso pré-selecionado ao abrir pela linha da grade. */
  initialResourceId?: string;
  isPending: boolean;
  errorMessage?: string;
  onSubmit: (input: AllocationSubmit) => void;
}

export function AllocationPanel({
  open,
  onOpenChange,
  rows,
  days,
  activities,
  services,
  initialResourceId,
  isPending,
  errorMessage,
  onSubmit,
}: AllocationPanelProps) {
  const allocatable = rows.filter((row) => row.resource.isAllocatable);
  const [resourceId, setResourceId] = useState(initialResourceId ?? '');
  const [activityId, setActivityId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [search, setSearch] = useState('');
  const [hoursByDate, setHoursByDate] = useState<Record<string, string>>({});
  const [quantityByDate, setQuantityByDate] = useState<Record<string, string>>({});
  /**
   * A recusa anterior já foi lida.
   *
   * A mensagem vem do caso de uso e vive na página, então ela continuava vermelha na tela
   * depois de a pessoa corrigir as horas — acusando um excesso que já não existia. Mexer
   * nos campos é o sinal de que a recusa foi entendida; a próxima tentativa traz a
   * resposta nova, se houver.
   */
  const [dismissedError, setDismissedError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setResourceId(initialResourceId ?? allocatable[0]?.resource.resourceId ?? '');
    setActivityId('');
    setServiceId('');
    setSearch('');
    setHoursByDate({});
    setQuantityByDate({});
    setDismissedError(false);
    // `allocatable` é derivado de `rows` a cada render; depender dele reabriria o efeito
    // sem necessidade. O que reabre o painel é `open` e o recurso escolhido na grade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialResourceId]);

  const byTower = useMemo(() => {
    const term = searchKey(search);
    const filtered =
      term === ''
        ? activities
        : activities.filter(
            (activity) =>
              searchKey(activity.label).includes(term) ||
              searchKey(activity.towerName).includes(term),
          );

    const groups = new Map<string, BoardActivity[]>();
    for (const activity of filtered) {
      const list = groups.get(activity.towerName);
      if (list === undefined) groups.set(activity.towerName, [activity]);
      else list.push(activity);
    }
    return [...groups.entries()];
  }, [activities, search]);

  const row = rows.find((item) => item.resource.resourceId === resourceId);
  const selected = activities.find((activity) => activity.activityId === activityId);

  /*
   * Os serviços da disciplina da frente — e só eles.
   *
   * É o filtro que o usuário pediu com outras palavras: procurar num campo de duzentos e
   * cinquenta é procurar; escolhida a frente, sobram cinco ou seis. A disciplina não
   * precisa de passo próprio porque a frente já a fixa — ela **é** pavimento × disciplina.
   *
   * Desativado fica de fora: ele não recebe planejamento novo (RN-230), e continua
   * nomeando a linha que já o tem.
   */
  const servicosDaFrente = useMemo(
    () =>
      selected === undefined
        ? []
        : services
            .filter((service) => service.disciplineId === selected.disciplineId && service.isActive)
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [services, selected],
  );

  const servicoEscolhido = servicosDaFrente.find((service) => service.serviceId === serviceId);

  /**
   * Trocar de frente limpa o serviço.
   *
   * Manter o anterior lançaria numa disciplina onde ele não existe — e o caso de uso
   * recusaria com "o serviço é de outra disciplina", o que é a recusa certa chegando tarde
   * demais para quem já digitou as horas.
   */
  useEffect(() => {
    setServiceId('');
  }, [activityId]);

  const parsedDays: AllocationDay[] = days.flatMap((day) => {
    const raw = (hoursByDate[day.date] ?? '').trim();
    if (raw === '') return [];
    const quarterHours = Math.round(Number(raw.replace(',', '.')) * QUARTERS_PER_HOUR);
    if (!Number.isFinite(quarterHours) || quarterHours <= 0) return [];

    // A meta só acompanha o dia que tem hora: prometer produção num dia sem ninguém
    // alocado seria uma promessa sem dono.
    const rawQuantity = (quantityByDate[day.date] ?? '').trim();
    const quantity = rawQuantity === '' ? undefined : Number(rawQuantity.replace(',', '.'));
    const validQuantity = quantity !== undefined && Number.isFinite(quantity) && quantity >= 0;

    return [{ date: day.date, quarterHours, ...(validQuantity ? { quantity } : {}) }];
  });

  const totalQuarterHours = parsedDays.reduce((sum, day) => sum + day.quarterHours, 0);
  const valid = resourceId !== '' && activityId !== '' && serviceId !== '' && parsedDays.length > 0;

  /**
   * A meta é do **serviço**, não do dia: trocar de serviço troca a unidade, e um número
   * digitado em m² não quer dizer nada em m³. Limpar é mais honesto que converter.
   */
  useEffect(() => {
    setQuantityByDate({});
  }, [activityId, serviceId]);

  const totalQuantity = parsedDays.reduce((sum, day) => sum + (day.quantity ?? 0), 0);

  /** Preenche o que ainda cabe em cada dia — a mesma ideia da distribuição do domínio. */
  const fillWorkdays = () => {
    if (row === undefined) return;
    const next: Record<string, string> = {};
    for (const [index, day] of days.entries()) {
      const total = row.dayTotals[index];
      if (total === undefined || total.capacityQuarterHours === undefined) continue;
      const room = total.capacityQuarterHours - total.plannedQuarterHours;
      if (room <= 0) continue;
      next[day.date] = String(room / QUARTERS_PER_HOUR);
    }
    setHoursByDate(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Adicionar alocação</DialogTitle>
          <DialogDescription>
            Escolha quem executa, em qual atividade, e quantas horas em cada dia. Os dias entram
            juntos — se um deles estourar a jornada, nenhum é gravado.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <Field label="Recurso" htmlFor="recurso" required>
            <select
              id="recurso"
              value={resourceId}
              onChange={(event) => {
                setResourceId(event.target.value);
                setDismissedError(true);
              }}
              className="h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-2 text-sm"
            >
              {allocatable.length === 0 && <option value="">Nenhum recurso disponível</option>}
              {allocatable.map((item) => (
                <option key={item.resource.resourceId} value={item.resource.resourceId}>
                  {item.resource.name} — {item.resource.detail}
                </option>
              ))}
            </select>
          </Field>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium">
                Atividade <span className="text-(--color-state-danger)">*</span>
              </span>
              <div className="relative flex items-center">
                <Search
                  className="pointer-events-none absolute left-2.5 size-4 text-(--color-content-muted)"
                  aria-hidden="true"
                />
                <Input
                  aria-label="Buscar atividade"
                  className="w-56 pl-8"
                  placeholder="Disciplina, pavimento ou torre…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            </div>

            {/*
              A lista de frentes, em duas colunas.

              Uma coluna só desperdiçava 850px: o nome ficava encolhido num canto e o
              resto da linha era espaço em branco. Aqui cada frente é um cartão com a
              disciplina em destaque, o pavimento embaixo e a unidade de medição à
              direita — e cabem o dobro de opções sem rolar.

              O agrupamento por torre continua sendo o cabeçalho: é assim que quem planeja
              procura a frente, e é o único dado que se repetiria em todos os cartões.
            */}
            <div className="max-h-72 overflow-y-auto rounded-(--radius-card) border border-(--color-border-subtle)">
              {byTower.length === 0 ? (
                <p className="p-4 text-sm text-(--color-content-muted)">
                  {search.trim() === ''
                    ? 'Nenhuma atividade disponível para alocação nesta obra.'
                    : 'Nenhuma frente corresponde à busca.'}
                </p>
              ) : (
                byTower.map(([tower, items]) => (
                  <div key={tower}>
                    <p className="sticky top-0 z-10 bg-(--color-surface-muted) px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-(--color-content-muted)">
                      {tower}
                    </p>
                    <ul className="grid gap-1.5 p-2 sm:grid-cols-2">
                      {items.map((activity) => {
                        const isSelected = activityId === activity.activityId;
                        return (
                          <li key={activity.activityId}>
                            <button
                              type="button"
                              aria-pressed={isSelected}
                              onClick={() => {
                                setActivityId(activity.activityId);
                                setDismissedError(true);
                              }}
                              className={cn(
                                'flex min-h-14 w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left transition-colors',
                                isSelected
                                  ? 'border-(--color-brand) bg-(--color-surface-muted)'
                                  : 'border-(--color-border-subtle) hover:bg-(--color-surface-muted)',
                              )}
                            >
                              <span className="min-w-0">
                                <span
                                  className={cn(
                                    'block truncate text-base',
                                    isSelected ? 'font-semibold' : 'font-medium',
                                  )}
                                >
                                  {activity.disciplineName}
                                </span>
                                <span className="block truncate text-sm text-(--color-content-muted)">
                                  {activity.floorName}
                                </span>
                              </span>

                              <span className="flex shrink-0 items-center gap-1.5">
                                {activity.hasBlocker === true && (
                                  <Badge tone="danger" icon={<AlertTriangle aria-hidden="true" />}>
                                    Impedida
                                  </Badge>
                                )}
                                {/*
                                  A unidade aparece aqui porque é ela que decide se o
                                  diálogo vai pedir meta depois — e descobrir isso só na
                                  hora de medir é tarde.
                                */}
                                {activity.unitName !== undefined && (
                                  <span className="rounded border border-(--color-border-subtle) px-1.5 py-0.5 text-xs text-(--color-content-muted)">
                                    {activity.unitName}
                                  </span>
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))
              )}
            </div>

            {/*
              O serviço, filtrado pela disciplina da frente.

              Aparece **depois** da frente porque depende dela: é a frente que diz qual
              disciplina, e a disciplina que reduz o catálogo a meia dúzia. Antes de
              escolher a frente ele nem existe — um select vazio e habilitado só ensina
              que a ordem está errada.
            */}
            {selected !== undefined && (
              <Field
                label="Serviço"
                htmlFor="servico"
                required
                {...(servicosDaFrente.length === 0
                  ? {
                      hint: `Nenhum serviço ativo em ${selected.disciplineName}. Cadastre em Configurações → Serviços.`,
                    }
                  : {})}
              >
                <select
                  id="servico"
                  className="h-11 w-full rounded-(--radius-field) border border-(--color-border-strong) bg-(--color-surface) px-3"
                  value={serviceId}
                  disabled={servicosDaFrente.length === 0}
                  onChange={(event) => {
                    setServiceId(event.target.value);
                    setDismissedError(true);
                  }}
                >
                  <option value="">Escolha o serviço…</option>
                  {servicosDaFrente.map((service) => (
                    <option key={service.serviceId} value={service.serviceId}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>

          {/*
            ── Horas por dia ──────────────────────────────────────────────────

            Sete campos numa linha só cabiam em 90px cada, com o dia escrito em 12px e o
            "cabe 9h" em 10px — o número mais importante do diálogo era o menos legível
            dele. Aqui cada dia é um cartão: o dia da semana em destaque, a data embaixo,
            o campo grande no meio e o que ainda cabe escrito por extenso.

            O bloco tem fundo e borda próprios porque é a terceira decisão do diálogo, e
            não um rodapé de campos soltos.
          */}
          <fieldset className="flex flex-col gap-3 rounded-(--radius-card) border border-(--color-border-subtle) bg-(--color-surface-muted) p-3 sm:p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <legend className="text-sm font-medium">
                Horas por dia <span className="text-(--color-state-danger)">*</span>
              </legend>
              <div className="flex flex-wrap items-center gap-3">
                {/* O total some quando é zero: um "0h" grande no topo não informa nada. */}
                {totalQuarterHours > 0 && (
                  <span className="tabular text-sm text-(--color-content-muted)">
                    {parsedDays.length} {parsedDays.length === 1 ? 'dia' : 'dias'} ·{' '}
                    <strong className="text-(--color-content)">
                      {Hours.ofQuarters(totalQuarterHours).format()}
                    </strong>{' '}
                    no total
                  </span>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={fillWorkdays}
                  disabled={row === undefined}
                >
                  <Wand2 aria-hidden="true" />
                  Preencher o que cabe
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
              {days.map((day, index) => {
                const total = row?.dayTotals[index];
                const value = hoursByDate[day.date] ?? '';
                const typed = value.trim() === '' ? 0 : Number(value.replace(',', '.'));
                const preview =
                  total === undefined
                    ? 'UNLIMITED'
                    : capacityStatusOf(
                        total.capacityQuarterHours,
                        total.plannedQuarterHours +
                          (Number.isFinite(typed) ? Math.max(0, typed) * QUARTERS_PER_HOUR : 0),
                      );
                const room =
                  total?.capacityQuarterHours === undefined
                    ? undefined
                    : Math.max(0, total.capacityQuarterHours - total.plannedQuarterHours);

                return (
                  <div
                    key={day.date}
                    className={cn(
                      'flex flex-col gap-1.5 rounded-md border p-2',
                      day.isWorkday
                        ? 'border-(--color-border-subtle) bg-(--color-surface)'
                        : 'border-transparent bg-(--color-surface-muted)',
                    )}
                  >
                    <label htmlFor={`dia-${day.date}`} className="text-center">
                      <span
                        className={cn(
                          'block text-sm font-semibold uppercase tracking-wide',
                          day.isWorkday ? 'text-(--color-content)' : 'text-(--color-content-muted)',
                        )}
                      >
                        {day.weekdayLabel}
                      </span>
                      <span className="block text-xs text-(--color-content-muted)">
                        {day.shortLabel}
                      </span>
                    </label>

                    <Input
                      id={`dia-${day.date}`}
                      type="number"
                      inputMode="decimal"
                      step={0.25}
                      min={0}
                      placeholder="—"
                      className={cn(
                        // `md:h-12` explícito: o `Input` encolhe para 36px no monitor, e
                        // aqui o campo é o alvo principal do diálogo em qualquer tela.
                        'tabular h-12 px-1 text-center text-base md:h-12',
                        preview === 'EXCEEDED' &&
                          'border-(--color-state-danger) text-(--color-state-danger)',
                      )}
                      value={value}
                      onChange={(event) => {
                        setHoursByDate((current) => ({
                          ...current,
                          [day.date]: event.target.value,
                        }));
                        setDismissedError(true);
                      }}
                    />

                    {/*
                      "cabe 0h" num sábado é tecnicamente verdade e praticamente ruído: o
                      canteiro não abre, e é isso que a pessoa precisa ler.
                    */}
                    <span
                      className={cn(
                        'block text-center text-xs',
                        preview === 'EXCEEDED'
                          ? 'font-medium text-(--color-state-danger)'
                          : 'text-(--color-content-muted)',
                      )}
                    >
                      {!day.isWorkday
                        ? 'não trabalha'
                        : room === undefined
                          ? 'sem limite'
                          : preview === 'EXCEEDED'
                            ? 'passa da jornada'
                            : `cabe ${Hours.ofQuarters(room).format()}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </fieldset>

          {/*
            ── Meta por dia ───────────────────────────────────────────────────

            Só existe quando a frente é medida. A grade semanal já pedia a meta numa
            linha própria, mas só **depois** de a alocação existir — quem lançava a
            semana inteira tinha de voltar campo a campo para dizer o que esperava de
            cada dia. Pedir aqui é pedir uma vez.

            Continua opcional: prometer produção é decisão de quem planeja, e meta em
            branco é um estado legítimo, não zero.
          */}
          {servicoEscolhido !== undefined && servicoEscolhido.unitName === undefined && (
            <p className="rounded-(--radius-card) border border-(--color-border-subtle) bg-(--color-surface-muted) p-3 text-sm text-(--color-content-muted)">
              <strong className="font-medium">{servicoEscolhido.name}</strong> não tem unidade de
              medição. Cadastre uma em Configurações → Serviços para poder planejar meta — as horas
              podem ser lançadas assim mesmo.
            </p>
          )}

          {servicoEscolhido?.unitName !== undefined && (
            <fieldset className="flex flex-col gap-3 rounded-(--radius-card) border border-(--color-border-subtle) p-3 sm:p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <legend className="text-sm font-medium">
                  Meta por dia{' '}
                  <span className="font-normal text-(--color-content-muted)">
                    em {servicoEscolhido.unitName} · opcional
                  </span>
                </legend>
                {totalQuantity > 0 && (
                  <span className="tabular text-sm text-(--color-content-muted)">
                    <strong className="text-(--color-content)">
                      {totalQuantity.toLocaleString('pt-BR', {
                        minimumFractionDigits: servicoEscolhido.unitDecimals ?? 2,
                        maximumFractionDigits: servicoEscolhido.unitDecimals ?? 2,
                      })}{' '}
                      {servicoEscolhido.unitName}
                    </strong>{' '}
                    na semana
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                {days.map((day) => {
                  const hasHours = (hoursByDate[day.date] ?? '').trim() !== '';
                  return (
                    <div key={day.date} className="flex flex-col gap-1">
                      <label
                        htmlFor={`meta-${day.date}`}
                        className="text-center text-xs font-medium uppercase tracking-wide text-(--color-content-muted)"
                      >
                        {day.weekdayLabel}
                      </label>
                      <Input
                        id={`meta-${day.date}`}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step={10 ** -(servicoEscolhido.unitDecimals ?? 2)}
                        placeholder="—"
                        // Sem hora no dia não há a quem prometer produção.
                        disabled={!hasHours}
                        aria-label={`Meta de ${day.weekdayLabel} ${day.shortLabel}, em ${servicoEscolhido.unitName}`}
                        className="tabular h-11 px-1 text-center text-sm md:h-11"
                        value={quantityByDate[day.date] ?? ''}
                        onChange={(event) => {
                          setQuantityByDate((current) => ({
                            ...current,
                            [day.date]: event.target.value,
                          }));
                          setDismissedError(true);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </fieldset>
          )}

          {errorMessage !== undefined && !dismissedError && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {errorMessage}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!valid || isPending}
            onClick={() =>
              onSubmit({
                resourceId,
                activityId,
                serviceId,
                days: parsedDays,
                ...(selected?.hasBlocker === true ? { acknowledgeRestriction: true } : {}),
              })
            }
          >
            Alocar{' '}
            {parsedDays.length > 0 &&
              `(${String(parsedDays.length)} ${parsedDays.length === 1 ? 'dia' : 'dias'})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
